import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { Copy, Plus, Trash2, Eye, EyeOff, Key, AlertTriangle, Webhook, BookOpen, RefreshCw } from "lucide-react";
import { PartnerApiReference } from "@/components/settings/PartnerApiReference";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { PARTNER_API_SCOPES } from "@/lib/partner-api";

const WEBHOOK_EVENTS = [
  { value: "lead.created", label: "Lead criado" },
  { value: "lead.updated", label: "Lead atualizado" },
  { value: "lead.deleted", label: "Lead excluído" },
  { value: "lead.stage_changed", label: "Lead mudou de estágio" },
  { value: "message.received", label: "Mensagem recebida" },
  { value: "message.sent", label: "Mensagem enviada" },
  { value: "conversation.created", label: "Conversa criada" },
  { value: "conversation.closed", label: "Conversa fechada" },
  { value: "product.created", label: "Produto criado" },
  { value: "product.updated", label: "Produto atualizado" },
  { value: "product.deleted", label: "Produto excluído" },
];

interface ApiKeyRow {
  id: string;
  name: string;
  key_prefix: string;
  scopes: string[];
  rate_limit_per_minute: number;
  last_used_at: string | null;
  expires_at: string | null;
  revoked_at: string | null;
  created_at: string;
}

interface WebhookRow {
  id: string;
  url: string;
  events: string[];
  is_active: boolean;
  failure_count: number;
  last_triggered_at: string | null;
  created_at: string;
}

export function ApiKeysManager({ workspaceId, userId }: { workspaceId: string; userId: string }) {
  const [apiKeys, setApiKeys] = useState<ApiKeyRow[]>([]);
  const [webhooks, setWebhooks] = useState<WebhookRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [newKeyScopes, setNewKeyScopes] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);
  const [generatedKey, setGeneratedKey] = useState<string | null>(null);
  const [revokeId, setRevokeId] = useState<string | null>(null);
  const [webhookDialogOpen, setWebhookDialogOpen] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [webhookEvents, setWebhookEvents] = useState<string[]>([]);
  const [creatingWebhook, setCreatingWebhook] = useState(false);
  const [webhookSecret, setWebhookSecret] = useState<string | null>(null);
  const [showSecret, setShowSecret] = useState(false);
  const { toast } = useToast();

  const fetchData = useCallback(async () => {
    setLoading(true);
    const [keysRes, webhooksRes] = await Promise.all([
      supabase.from("api_keys").select("id, name, key_prefix, scopes, rate_limit_per_minute, last_used_at, expires_at, revoked_at, created_at").eq("workspace_id", workspaceId).order("created_at", { ascending: false }),
      supabase.from("webhook_subscriptions").select("id, url, events, is_active, failure_count, last_triggered_at, created_at").eq("workspace_id", workspaceId).order("created_at", { ascending: false }),
    ]);
    setApiKeys((keysRes.data as any[]) || []);
    setWebhooks((webhooksRes.data as any[]) || []);
    setLoading(false);
  }, [workspaceId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const generateApiKey = (): string => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let result = "fab_";
    for (let i = 0; i < 40; i++) result += chars.charAt(Math.floor(Math.random() * chars.length));
    return result;
  };

  const hashKey = async (key: string): Promise<string> => {
    const encoder = new TextEncoder();
    const data = encoder.encode(key);
    const hash = await crypto.subtle.digest("SHA-256", data);
    return Array.from(new Uint8Array(hash)).map(b => b.toString(16).padStart(2, "0")).join("");
  };

  const handleCreate = async () => {
    if (!newKeyName.trim()) {
      toast({ title: "Nome obrigatório", variant: "destructive" });
      return;
    }
    if (newKeyScopes.length === 0) {
      toast({ title: "Selecione ao menos um escopo", variant: "destructive" });
      return;
    }

    setCreating(true);
    try {
      const rawKey = generateApiKey();
      const keyHash = await hashKey(rawKey);
      const keyPrefix = rawKey.substring(0, 8);

      const { error } = await supabase.from("api_keys").insert({
        workspace_id: workspaceId,
        key_hash: keyHash,
        key_prefix: keyPrefix,
        name: newKeyName.trim(),
        scopes: newKeyScopes,
        created_by: userId,
      });

      if (error) throw error;

      setGeneratedKey(rawKey);
      toast({ title: "API Key criada com sucesso!" });
      fetchData();
    } catch (err: any) {
      toast({ title: "Erro ao criar API Key", description: err.message, variant: "destructive" });
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (id: string) => {
    const { error } = await supabase.from("api_keys").update({ revoked_at: new Date().toISOString() }).eq("id", id);
    if (error) {
      toast({ title: "Erro ao revogar", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "API Key revogada" });
      fetchData();
    }
    setRevokeId(null);
  };

  const handleCreateWebhook = async () => {
    if (!webhookUrl.trim()) {
      toast({ title: "URL obrigatória", variant: "destructive" });
      return;
    }
    if (webhookEvents.length === 0) {
      toast({ title: "Selecione ao menos um evento", variant: "destructive" });
      return;
    }

    setCreatingWebhook(true);
    try {
      const secretBytes = crypto.getRandomValues(new Uint8Array(32));
      const secret = Array.from(secretBytes).map(b => b.toString(16).padStart(2, "0")).join("");

      const { error } = await supabase.from("webhook_subscriptions").insert({
        workspace_id: workspaceId,
        url: webhookUrl.trim(),
        events: webhookEvents,
        secret,
      });

      if (error) throw error;

      setWebhookSecret(secret);
      toast({ title: "Webhook criado com sucesso!" });
      fetchData();
    } catch (err: any) {
      toast({ title: "Erro ao criar webhook", description: err.message, variant: "destructive" });
    } finally {
      setCreatingWebhook(false);
    }
  };

  const handleDeleteWebhook = async (id: string) => {
    const { error } = await supabase.from("webhook_subscriptions").delete().eq("id", id);
    if (error) {
      toast({ title: "Erro ao excluir webhook", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Webhook excluído" });
      fetchData();
    }
  };

  const handleToggleWebhook = async (id: string, isActive: boolean) => {
    const { error } = await supabase.from("webhook_subscriptions").update({ is_active: !isActive }).eq("id", id);
    if (error) {
      toast({ title: "Erro", description: error.message, variant: "destructive" });
    } else {
      fetchData();
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({ title: "Copiado!" });
  };

  const toggleScope = (scope: string) => {
    setNewKeyScopes(prev => prev.includes(scope) ? prev.filter(s => s !== scope) : [...prev, scope]);
  };

  const toggleWebhookEvent = (event: string) => {
    setWebhookEvents(prev => prev.includes(event) ? prev.filter(e => e !== event) : [...prev, event]);
  };

  const selectAllScopes = () => setNewKeyScopes(PARTNER_API_SCOPES.map(s => s.value));
  const activeKeys = apiKeys.filter(k => !k.revoked_at);
  const revokedKeys = apiKeys.filter(k => k.revoked_at);

  const baseUrl = `https://tzgfynjxvuvuymfxicmf.supabase.co/functions/v1/api-gateway`;

  if (loading) return <p className="text-sm text-muted-foreground text-center p-4">Carregando...</p>;

  return (
    <div className="space-y-4">
      <Tabs defaultValue="keys">
        <TabsList className="w-full">
          <TabsTrigger value="keys" className="flex-1">
            <Key className="w-4 h-4 mr-1.5" />
            API Keys
          </TabsTrigger>
          <TabsTrigger value="webhooks" className="flex-1">
            <Webhook className="w-4 h-4 mr-1.5" />
            Webhooks
          </TabsTrigger>
          <TabsTrigger value="docs" className="flex-1">
            <BookOpen className="w-4 h-4 mr-1.5" />
            Referência
          </TabsTrigger>
        </TabsList>

        {/* API Keys Tab */}
        <TabsContent value="keys" className="space-y-4 mt-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">{activeKeys.length} chave(s) ativa(s)</p>
            <Button size="sm" onClick={() => { setCreateDialogOpen(true); setNewKeyName(""); setNewKeyScopes([]); setGeneratedKey(null); }}>
              <Plus className="w-4 h-4 mr-1.5" />
              Nova API Key
            </Button>
          </div>

          {activeKeys.length === 0 ? (
            <div className="text-center p-6 bg-muted/50 rounded-lg">
              <Key className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">Nenhuma API Key ativa</p>
              <p className="text-xs text-muted-foreground mt-1">Crie uma chave para integrar sistemas externos</p>
            </div>
          ) : (
            <div className="space-y-2">
              {activeKeys.map(key => (
                <div key={key.id} className="p-3 bg-muted/50 rounded-lg flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div className="space-y-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm truncate">{key.name}</span>
                      <Badge variant="outline" className="text-[10px] shrink-0">{key.key_prefix}...</Badge>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {key.scopes.slice(0, 4).map(s => (
                        <Badge key={s} variant="secondary" className="text-[10px]">{s}</Badge>
                      ))}
                      {key.scopes.length > 4 && <Badge variant="secondary" className="text-[10px]">+{key.scopes.length - 4}</Badge>}
                    </div>
                    <p className="text-[10px] text-muted-foreground">
                      {key.last_used_at ? `Último uso: ${new Date(key.last_used_at).toLocaleDateString("pt-BR")}` : "Nunca utilizada"}
                    </p>
                  </div>
                  <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive shrink-0" onClick={() => setRevokeId(key.id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {revokedKeys.length > 0 && (
            <div className="pt-2">
              <p className="text-xs text-muted-foreground mb-2">{revokedKeys.length} chave(s) revogada(s)</p>
              {revokedKeys.map(key => (
                <div key={key.id} className="p-2 bg-muted/30 rounded-lg flex items-center justify-between opacity-50">
                  <div className="flex items-center gap-2">
                    <span className="text-sm line-through">{key.name}</span>
                    <Badge variant="outline" className="text-[10px]">{key.key_prefix}...</Badge>
                    <Badge variant="destructive" className="text-[10px]">Revogada</Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Webhooks Tab */}
        <TabsContent value="webhooks" className="space-y-4 mt-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">{webhooks.length} webhook(s)</p>
            <Button size="sm" onClick={() => { setWebhookDialogOpen(true); setWebhookUrl(""); setWebhookEvents([]); setWebhookSecret(null); }}>
              <Plus className="w-4 h-4 mr-1.5" />
              Novo Webhook
            </Button>
          </div>

          {webhooks.length === 0 ? (
            <div className="text-center p-6 bg-muted/50 rounded-lg">
              <Webhook className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">Nenhum webhook configurado</p>
              <p className="text-xs text-muted-foreground mt-1">Receba notificações em tempo real no seu sistema</p>
            </div>
          ) : (
            <div className="space-y-2">
              {webhooks.map(wh => (
                <div key={wh.id} className="p-3 bg-muted/50 rounded-lg space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <Badge variant={wh.is_active ? "default" : "secondary"} className="text-[10px] shrink-0">
                        {wh.is_active ? "Ativo" : "Inativo"}
                      </Badge>
                      <span className="text-sm truncate font-mono">{wh.url}</span>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button variant="ghost" size="sm" onClick={() => handleToggleWebhook(wh.id, wh.is_active)}>
                        <RefreshCw className="w-3 h-3" />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => handleDeleteWebhook(wh.id)}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {wh.events.map(e => <Badge key={e} variant="outline" className="text-[10px]">{e}</Badge>)}
                  </div>
                  {wh.failure_count > 0 && (
                    <p className="text-[10px] text-destructive flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" /> {wh.failure_count} falha(s)
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="docs" className="space-y-4 mt-4">
          <PartnerApiReference workspaceId={workspaceId} baseUrl={baseUrl} />
        </TabsContent>
      </Tabs>

      {/* Create API Key Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={(open) => { if (!open) { setCreateDialogOpen(false); setGeneratedKey(null); } }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{generatedKey ? "API Key Criada" : "Nova API Key"}</DialogTitle>
            <DialogDescription>
              {generatedKey ? "Copie a chave abaixo. Ela não será exibida novamente." : "Configure o nome e as permissões da chave."}
            </DialogDescription>
          </DialogHeader>

          {generatedKey ? (
            <div className="space-y-4">
              <div className="p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-xs text-amber-700 dark:text-amber-400">Esta chave só será exibida uma vez. Copie e guarde em local seguro.</p>
              </div>
              <div className="flex items-center gap-2">
                <Input value={generatedKey} readOnly className="font-mono text-xs" />
                <Button variant="outline" size="sm" onClick={() => copyToClipboard(generatedKey)}>
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <DialogFooter>
                <Button onClick={() => { setCreateDialogOpen(false); setGeneratedKey(null); }}>Fechar</Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Nome da chave</Label>
                <Input placeholder="Ex: ERP Produção" value={newKeyName} onChange={e => setNewKeyName(e.target.value)} />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Permissões (scopes)</Label>
                  <Button variant="ghost" size="sm" className="text-xs h-6" onClick={selectAllScopes}>Selecionar todos</Button>
                </div>
                <div className="grid grid-cols-1 gap-1.5 max-h-60 overflow-y-auto p-2 bg-muted/50 rounded-lg">
                  {PARTNER_API_SCOPES.map(scope => (
                    <label key={scope.value} className="flex items-center gap-2 cursor-pointer hover:bg-muted/80 p-1.5 rounded text-sm">
                      <Checkbox checked={newKeyScopes.includes(scope.value)} onCheckedChange={() => toggleScope(scope.value)} />
                      <span className="flex-1">{scope.label}</span>
                      <Badge variant="outline" className="text-[9px]">{scope.group}</Badge>
                    </label>
                  ))}
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancelar</Button>
                <Button onClick={handleCreate} disabled={creating}>
                  {creating ? "Criando..." : "Criar API Key"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Revoke Confirmation */}
      <AlertDialog open={!!revokeId} onOpenChange={() => setRevokeId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revogar API Key?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação é irreversível. Sistemas que usam esta chave perderão acesso imediatamente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => revokeId && handleRevoke(revokeId)} className="bg-destructive hover:bg-destructive/90">
              Revogar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create Webhook Dialog */}
      <Dialog open={webhookDialogOpen} onOpenChange={(open) => { if (!open) { setWebhookDialogOpen(false); setWebhookSecret(null); } }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{webhookSecret ? "Webhook Criado" : "Novo Webhook"}</DialogTitle>
            <DialogDescription>
              {webhookSecret ? "Copie o secret HMAC para validar as assinaturas." : "Configure a URL e eventos do webhook."}
            </DialogDescription>
          </DialogHeader>

          {webhookSecret ? (
            <div className="space-y-4">
              <div className="p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-xs text-amber-700 dark:text-amber-400">O secret HMAC só será exibido uma vez. Use-o para validar assinaturas (X-Webhook-Signature).</p>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">HMAC Secret</Label>
                <div className="flex items-center gap-2">
                  <Input value={showSecret ? webhookSecret : "••••••••••••"} readOnly className="font-mono text-xs" />
                  <Button variant="ghost" size="sm" onClick={() => setShowSecret(!showSecret)}>
                    {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => copyToClipboard(webhookSecret)}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={() => { setWebhookDialogOpen(false); setWebhookSecret(null); setShowSecret(false); }}>Fechar</Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>URL do Webhook</Label>
                <Input placeholder="https://seu-sistema.com/webhook" value={webhookUrl} onChange={e => setWebhookUrl(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Eventos</Label>
                <div className="grid grid-cols-1 gap-1.5 max-h-48 overflow-y-auto p-2 bg-muted/50 rounded-lg">
                  {WEBHOOK_EVENTS.map(event => (
                    <label key={event.value} className="flex items-center gap-2 cursor-pointer hover:bg-muted/80 p-1.5 rounded text-sm">
                      <Checkbox checked={webhookEvents.includes(event.value)} onCheckedChange={() => toggleWebhookEvent(event.value)} />
                      <span>{event.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setWebhookDialogOpen(false)}>Cancelar</Button>
                <Button onClick={handleCreateWebhook} disabled={creatingWebhook}>
                  {creatingWebhook ? "Criando..." : "Criar Webhook"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
